import { Box, Text } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';

interface GeneratedTextProps {
  text: string;
  isGenerating: boolean;
}

export const GeneratedText = ({ text, isGenerating }: GeneratedTextProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasUserScrolled = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.focus();
  }, []);

  // Add event listener to detect if user has scrolled
  // If they have scrolled during generation, don't automatically scroll to bottom
  useEffect(() => {
    if (!containerRef.current) return;

    const containerEl = containerRef.current;
    const setScrollOn = () => {
      hasUserScrolled.current = containerEl.scrollTop + containerEl.clientHeight !== containerEl.scrollHeight;
    };

    containerRef.current.addEventListener('scroll', setScrollOn);

    return () => {
      containerEl.removeEventListener('scroll', setScrollOn);
      hasUserScrolled.current = false;
    };
  }, []);

  // Reset flag when there is a new generation and focus on container
  useEffect(() => {
    hasUserScrolled.current = false;

    if (!containerRef.current) return;
    containerRef.current.focus();
  }, [isGenerating]);

  // Scroll to bottom when text changes unless user has scrolled during generation
  useEffect(() => {
    if (!containerRef.current) return;
    if (hasUserScrolled.current) return;
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [text]);

  return (
    // Add tab index so can scroll with keyboard
    // Ref: https://accessibilityinsights.io/info-examples/web/scrollable-region-focusable/
    <Box ref={containerRef} tabIndex={0} overflowY='auto' maxH='200px' minH={0}>
      <Text whiteSpace='pre-line'>{text}</Text>
    </Box>
  );
};
